import { useEffect, useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Typography, Row, Col, Card, Spin, Empty, Breadcrumb, Tag, Space, Result, Button } from 'antd'
import { apiRequest } from '../utils/apiRequest'
import { useCurrency } from '../contexts/CurrencyContext'
import Seo, { siteBase } from '../components/Seo'
import { buildItemListJsonLd, buildBreadcrumbJsonLd, slugifyCategory } from '../lib/seo'

const { Title, Paragraph, Text } = Typography

/**
 * Crawlable landing page for one storefront navigation section.
 *
 * The homepage network tabs are client-side state, which search engines cannot
 * index and customers cannot link to. This gives every section its own URL
 * (/network/econet-whatsapp-data), with real meta, an ItemList of what it holds
 * and a breadcrumb — so the taxonomy is an SEO surface rather than just a widget.
 *
 * Serves both levels: a network tab lists its child sections, a child section
 * lists products. Slugs are unique across both, so one route covers both.
 */
export default function NavSectionPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { formatMoney } = useCurrency()

  const [section, setSection] = useState(null)
  const [children, setChildren] = useState([])
  const [items, setItems] = useState([])
  const [state, setState] = useState('loading') // loading | ready | missing

  useEffect(() => {
    let cancelled = false
    setState('loading')

    const load = async () => {
      try {
        // The tree gives us the section's children and its parent, which the
        // items endpoint alone cannot provide.
        const [treeRes, itemsRes] = await Promise.allSettled([
          apiRequest({ method: 'get', url: '/nav_sections', suppressNotifications: true }),
          apiRequest({ method: 'get', url: `/nav_sections/${encodeURIComponent(slug)}/items?limit=60`, suppressNotifications: true }),
        ])
        if (cancelled) return

        const tree = treeRes.status === 'fulfilled' ? (treeRes.value.data?.sections || []) : []
        let found = null
        let kids = []
        let parentOf = null

        for (const network of tree) {
          if (network.slug === slug) { found = network; kids = network.children || [] ; break }
          const child = (network.children || []).find((c) => c.slug === slug)
          if (child) { found = child; parentOf = network; break }
        }

        const fetched = itemsRes.status === 'fulfilled' ? (itemsRes.value.data?.items || []) : []
        const resolved = found || (itemsRes.status === 'fulfilled' ? itemsRes.value.data?.section : null)

        if (!resolved) { setState('missing'); return }

        setSection({ ...resolved, parent: parentOf })
        setChildren(kids)
        setItems(fetched)
        setState('ready')
      } catch {
        if (!cancelled) setState('missing')
      }
    }

    load()
    return () => { cancelled = true }
  }, [slug])

  const linkFor = (item) => (
    item.item_type === 'category'
      ? `/buy/${slugifyCategory(item.name)}`
      : `/product/${item.id}`
  )

  const title = useMemo(() => {
    if (!section) return 'Browse — Tanaka Life'
    const base = section.parent
      ? `${section.parent.name} ${section.name}`
      : `${section.name} Airtime & Data`
    return `Buy ${base} Online in South Africa | Tanaka Life`
  }, [section])

  const description = useMemo(() => {
    if (!section) return ''
    return section.description
      || `Buy ${section.name} online with instant delivery to Zimbabwe. Pay by card, instant EFT or wallet.`
  }, [section])

  const jsonLd = useMemo(() => {
    if (!section) return null
    const crumbs = [{ name: 'Home', url: '/' }]
    if (section.parent) crumbs.push({ name: section.parent.name, url: `/network/${section.parent.slug}` })
    crumbs.push({ name: section.name, url: `/network/${section.slug}` })

    const listed = children.length
      ? children.map((c) => ({ name: c.name, url: `/network/${c.slug}` }))
      : items.map((i) => ({ name: String(i.name).trim(), url: linkFor(i) }))

    return [
      buildBreadcrumbJsonLd(crumbs, siteBase),
      buildItemListJsonLd(listed, siteBase),
    ]
  }, [section, children, items])

  if (state === 'missing') {
    return (
      <>
        <Seo title="Not found — Tanaka Life" noindex />
        <Result
          status="404"
          title="We couldn't find that section"
          subTitle="It may have been renamed or removed."
          extra={<Button type="primary" onClick={() => navigate('/categories')}>Browse everything</Button>}
        />
      </>
    )
  }

  if (state === 'loading') {
    return <div style={{ textAlign: 'center', padding: '4rem 0' }}><Spin size="large" /></div>
  }

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={`${siteBase}/network/${section.slug}`}
        jsonLd={jsonLd}
      />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            { title: <Link to="/">Home</Link> },
            ...(section.parent ? [{ title: <Link to={`/network/${section.parent.slug}`}>{section.parent.name}</Link> }] : []),
            { title: section.name },
          ]}
        />

        <Title level={1} style={{ fontSize: '1.8rem' }}>
          {section.parent ? `${section.parent.name} ${section.name}` : section.name}
        </Title>
        <Paragraph type="secondary">{description}</Paragraph>

        {/* A network tab links to its sections — real anchors, so crawlers and
            customers can both reach them. */}
        {children.length > 0 && (
          <Space size={[8, 8]} wrap style={{ marginBottom: 24 }}>
            {children.map((child) => (
              <Link key={child.slug} to={`/network/${child.slug}`}>
                <Tag style={{ padding: '6px 12px', fontSize: 14, cursor: 'pointer' }}>
                  {child.name}{child.item_count ? ` (${child.item_count})` : ''}
                </Tag>
              </Link>
            ))}
          </Space>
        )}

        {items.length === 0 && children.length === 0 ? (
          <Empty description="Nothing available here right now" />
        ) : (
          <Row gutter={[16, 16]}>
            {items.map((item) => (
              <Col xs={12} sm={8} md={6} lg={4} key={`${item.item_type}-${item.id}`}>
                {/* A real <Link>, not an onClick: this is what passes crawlable
                    link equity down to the product pages. */}
                <Link to={linkFor(item)}>
                  <Card hoverable size="small" style={{ height: '100%' }}>
                    <Text strong style={{ display: 'block', fontSize: 13 }}>
                      {String(item.name).trim()}
                    </Text>
                    <Paragraph type="secondary" style={{ fontSize: 11, margin: '4px 0 8px' }}>
                      {item.provider_name}
                    </Paragraph>
                    <Text strong>
                      {item.item_type === 'category'
                        ? 'Any amount'
                        : formatMoney(Number(item.price || 0), item.currency)}
                    </Text>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>
        )}
      </div>
    </>
  )
}
